import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, Text, Tooltip, TooltipTrigger, TruncatedText } from '@/components/core';
import { DocumentIcon, ImageIcon, PDFIcon, RejectIcon, SaveIcon } from '@/components/icons';
import { useAgentRunTree } from '@/hooks';
import { toAgentRunId, useGetAgentRun } from '@/hooks/api';
import { DivProps } from '@/utils';

export type AgentRunDocumentCardProps = DivProps & {
  agentId: string;
  runId: string;
};

const IAgentRunDocumentCard = ({ agentId, runId, className, ...rest }: AgentRunDocumentCardProps) => {
  const { taskDocument, predictionEvents, isDocumentsDeleted } = useAgentRunTree({ agentId, runId });
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));

  const isPredicting = useMemo(() => {
    if (predictionEvents.length > 0) {
      return run?.status === 'running' && predictionEvents[predictionEvents.length - 1].status === 'pending';
    }
    return true;
  }, [predictionEvents, run?.status]);

  if (isDocumentsDeleted) {
    return (
      <TooltipTrigger>
        <Tooltip variant="primary">
          <Text>Document was either deleted or expired due to retention settings</Text>
        </Tooltip>

        <Focusable>
          <Flex className={merge('gap-sm items-center', className)} {...rest}>
            <Flex className="gap-sm items-center justify-center p-2">
              <DocumentIcon className="absolute size-[1.6875rem]" />
              <RejectIcon className="text-error-500 !size-[0.625rem] translate-y-[4px]" />
            </Flex>
            <TruncatedText className="text-gray-800">Document not found</TruncatedText>
          </Flex>
        </Focusable>
      </TooltipTrigger>
    );
  }

  const loadingDocument = (
    <Flex className="gap-sm items-center justify-center p-2">
      <DocumentIcon className="absolute size-[1.6875rem]" />
      <SaveIcon
        className={merge('!size-[0.625rem] translate-y-[4px]', taskDocument && isPredicting && 'text-primary-500')}
      />
    </Flex>
  );

  if (!taskDocument) {
    return (
      <Flex className={merge('items-center', className)} {...rest}>
        {loadingDocument}
        <div className="ml-[9px] h-3 w-48 animate-pulse rounded-lg bg-gray-200" />
      </Flex>
    );
  }

  return (
    <Flex className={merge('gap-sm items-center', className)} {...rest}>
      {isPredicting ? (
        loadingDocument
      ) : taskDocument.contentType === 'application/pdf' ? (
        <PDFIcon className="min-h-[1.6875rem] min-w-[1.6875rem]" />
      ) : (
        <ImageIcon className="min-h-[1.6875rem] min-w-[1.6875rem]" />
      )}
      <TruncatedText className="text-gray-800">{taskDocument.name}</TruncatedText>
    </Flex>
  );
};

export const AgentRunDocumentCard = memo(IAgentRunDocumentCard) as typeof IAgentRunDocumentCard;
